import { type MetaFunction } from '@remix-run/node'
import { Button } from '#app/components/buttons/button'
import { Container } from '#app/components/layout/container'
import { Flex } from '#app/components/layout/flex'
import { H1 } from '#app/components/typography/h1'
import { H2 } from '#app/components/typography/h2'
import { useIsomorphicLayoutEffect } from '#app/hooks/useIsomorphicLayoutEffect.js'

export const meta: MetaFunction = () => {
	return [{ name: 'robots', content: `noindex,nofollow` }]
}

export default function UILibraryTestPage() {
	useIsomorphicLayoutEffect(() => {
		const componentRules = Object.entries(document.styleSheets[0].cssRules).filter(([_, rule]) => (rule as CSSLayerBlockRule).name === 'components')[0][1]
		const buttonStyles = Object.entries((componentRules as CSSLayerBlockRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '.button')[0][1]
		const buttonPrimaryStyles = Object.entries((buttonStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&.primary')[0][1]
		const buttonSecondaryStyles = Object.entries((buttonStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&.secondary')[0][1]
		const buttonDangerStyles = Object.entries((buttonStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&.danger')[0][1]
		const buttonGhostStyles = Object.entries((buttonStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&.ghost')[0][1]

		const buttonPrimaryHoveredStyles = (
			Object.entries((buttonPrimaryStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&[data-hovered]')[0][1] as CSSStyleRule
		).style
		const buttonSecondaryHoveredStyles = (
			Object.entries((buttonSecondaryStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&[data-hovered]')[0][1] as CSSStyleRule
		).style
		const buttonDangerHoveredStyles = (
			Object.entries((buttonDangerStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&[data-hovered]')[0][1] as CSSStyleRule
		).style
		const buttonGhostHoveredStyles = (
			Object.entries((buttonGhostStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&[data-hovered]')[0][1] as CSSStyleRule
		).style

		const buttonPrimaryPressedStyles = (
			Object.entries((buttonPrimaryStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&[data-pressed]')[0][1] as CSSStyleRule
		).style
		const buttonSecondaryPressedStyles = (
			Object.entries((buttonSecondaryStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&[data-pressed]')[0][1] as CSSStyleRule
		).style
		const buttonDangerPressedStyles = (
			Object.entries((buttonDangerStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&[data-pressed]')[0][1] as CSSStyleRule
		).style
		const buttonGhostPressedStyles = (
			Object.entries((buttonGhostStyles as CSSStyleRule).cssRules).filter(([_, rule]) => (rule as CSSStyleRule).selectorText === '&[data-pressed]')[0][1] as CSSStyleRule
		).style

		const buttonLightPrimaryHovered = document.querySelector('[data-theme="light"] #hovered button.primary') as HTMLElement
		const buttonLightSecondaryHovered = document.querySelector('[data-theme="light"] #hovered button.secondary') as HTMLElement
		const buttonLightDangerHovered = document.querySelector('[data-theme="light"] #hovered button.danger') as HTMLElement
		const buttonLightDisabledHovered = document.querySelector('[data-theme="light"] #hovered button.ghost') as HTMLElement
		const buttonLightPrimaryPressed = document.querySelector('[data-theme="light"] #pressed button.primary') as HTMLElement
		const buttonLightSecondaryPressed = document.querySelector('[data-theme="light"] #pressed button.secondary') as HTMLElement
		const buttonLightDangerPressed = document.querySelector('[data-theme="light"] #pressed button.danger') as HTMLElement
		const buttonLightDisabledPressed = document.querySelector('[data-theme="light"] #pressed button.ghost') as HTMLElement

		const buttonDarkPrimaryHovered = document.querySelector('[data-theme="dark"] #hovered button.primary') as HTMLElement
		const buttonDarkSecondaryHovered = document.querySelector('[data-theme="dark"] #hovered button.secondary') as HTMLElement
		const buttonDarkDangerHovered = document.querySelector('[data-theme="dark"] #hovered button.danger') as HTMLElement
		const buttonDarkDisabledHovered = document.querySelector('[data-theme="dark"] #hovered button.ghost') as HTMLElement
		const buttonDarkPrimaryPressed = document.querySelector('[data-theme="dark"] #pressed button.primary') as HTMLElement
		const buttonDarkSecondaryPressed = document.querySelector('[data-theme="dark"] #pressed button.secondary') as HTMLElement
		const buttonDarkDangerPressed = document.querySelector('[data-theme="dark"] #pressed button.danger') as HTMLElement
		const buttonDarkDisabledPressed = document.querySelector('[data-theme="dark"] #pressed button.ghost') as HTMLElement

		buttonLightPrimaryHovered.setAttribute('style', buttonPrimaryHoveredStyles.cssText)
		buttonLightSecondaryHovered.setAttribute('style', buttonSecondaryHoveredStyles.cssText)
		buttonLightDangerHovered.setAttribute('style', buttonDangerHoveredStyles.cssText)
		buttonLightDisabledHovered.setAttribute('style', buttonGhostHoveredStyles.cssText)
		buttonLightPrimaryPressed.setAttribute('style', buttonPrimaryPressedStyles.cssText)
		buttonLightSecondaryPressed.setAttribute('style', buttonSecondaryPressedStyles.cssText)
		buttonLightDangerPressed.setAttribute('style', buttonDangerPressedStyles.cssText)
		buttonLightDisabledPressed.setAttribute('style', buttonGhostPressedStyles.cssText)
		buttonDarkPrimaryHovered.setAttribute('style', buttonPrimaryHoveredStyles.cssText)
		buttonDarkSecondaryHovered.setAttribute('style', buttonSecondaryHoveredStyles.cssText)
		buttonDarkDangerHovered.setAttribute('style', buttonDangerHoveredStyles.cssText)
		buttonDarkDisabledHovered.setAttribute('style', buttonGhostHoveredStyles.cssText)
		buttonDarkPrimaryPressed.setAttribute('style', buttonPrimaryPressedStyles.cssText)
		buttonDarkSecondaryPressed.setAttribute('style', buttonSecondaryPressedStyles.cssText)
		buttonDarkDangerPressed.setAttribute('style', buttonDangerPressedStyles.cssText)
		buttonDarkDisabledPressed.setAttribute('style', buttonGhostPressedStyles.cssText)
	})

	return (
		<Container>
			<H1>Color Contrast Test Page</H1>
			<Flex>
				<Flex orientation="vertical" gap="10" data-theme="light" className="bg-background text-foreground rounded-xl px-5 pb-5">
					<H2>Button</H2>
					<Flex gap="10" id="normal">
						<Button size="md" variant="primary" className="w-28">
							Primary
						</Button>
						<Button size="md" variant="secondary" className="w-28">
							Secondary
						</Button>
						<Button size="md" variant="danger" className="w-28">
							Danger
						</Button>
						<Button size="md" variant="ghost" className="w-28">
							Ghost
						</Button>
					</Flex>
					<Flex gap="10" id="hovered">
						<Button size="md" variant="primary" className="w-28">
							Hovered
						</Button>
						<Button size="md" variant="secondary" className="w-28">
							Hovered
						</Button>
						<Button size="md" variant="danger" className="w-28">
							Hovered
						</Button>
						<Button size="md" variant="ghost" className="w-28">
							Hovered
						</Button>
					</Flex>
					<Flex gap="10" id="pressed">
						<Button size="md" variant="primary" className="w-28">
							Pressed
						</Button>
						<Button size="md" variant="secondary" className="w-28">
							Pressed
						</Button>
						<Button size="md" variant="danger" className="w-28">
							Pressed
						</Button>
						<Button size="md" variant="ghost" className="w-28">
							Pressed
						</Button>
					</Flex>
					<Flex gap="10" id="disabled">
						<Button size="md" variant="primary" className="w-28" isDisabled>
							Disabled
						</Button>
						<Button size="md" variant="secondary" className="w-28" isDisabled>
							Disabled
						</Button>
						<Button size="md" variant="danger" className="w-28" isDisabled>
							Disabled
						</Button>
						<Button size="md" variant="ghost" className="w-28" isDisabled>
							Disabled
						</Button>
					</Flex>
				</Flex>
				<Flex orientation="vertical" gap="10" data-theme="dark" className="bg-background text-foreground rounded-xl px-5 pb-5">
					<H2>Button</H2>
					<Flex gap="10" id="normal">
						<Button size="md" variant="primary" className="w-28">
							Primary
						</Button>
						<Button size="md" variant="secondary" className="w-28">
							Secondary
						</Button>
						<Button size="md" variant="danger" className="w-28">
							Danger
						</Button>
						<Button size="md" variant="ghost" className="w-28">
							Ghost
						</Button>
					</Flex>
					<Flex gap="10" id="hovered">
						<Button size="md" variant="primary" className="w-28">
							Hovered
						</Button>
						<Button size="md" variant="secondary" className="w-28">
							Hovered
						</Button>
						<Button size="md" variant="danger" className="w-28">
							Hovered
						</Button>
						<Button size="md" variant="ghost" className="w-28">
							Hovered
						</Button>
					</Flex>
					<Flex gap="10" id="pressed">
						<Button size="md" variant="primary" className="w-28">
							Pressed
						</Button>
						<Button size="md" variant="secondary" className="w-28">
							Pressed
						</Button>
						<Button size="md" variant="danger" className="w-28">
							Pressed
						</Button>
						<Button size="md" variant="ghost" className="w-28">
							Pressed
						</Button>
					</Flex>
					<Flex gap="10" id="disabled">
						<Button size="md" variant="primary" className="w-28" isDisabled>
							Disabled
						</Button>
						<Button size="md" variant="secondary" className="w-28" isDisabled>
							Disabled
						</Button>
						<Button size="md" variant="danger" className="w-28" isDisabled>
							Disabled
						</Button>
						<Button size="md" variant="ghost" className="w-28" isDisabled>
							Disabled
						</Button>
					</Flex>
				</Flex>
			</Flex>
		</Container>
	)
}
